import { Component, computed, DOCUMENT, effect, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { UiButtonComponent, UiEmptyStateComponent, UIIconComponent, UiLoadingComponent } from '@design-system';
import { guid } from '@tools';
import { catchError, concat, EMPTY, Observable, of, timer } from 'rxjs';
import { map, scan, switchMap } from 'rxjs/operators';
import { AccordionComponent } from '../../../../components/accordion/accordion.component';
import { IntradayChartPoint } from '../../../../models/intraday-chart.model';
import { SYMBOL_DETAIL_SECTION_LABELS } from '../../../../models/symbol-detail-section.model';
import { SymbolDetailLayoutStore } from '../../data-access/symbol-detail-layout.store';
import { SymbolsService } from '../../../../services/symbols.service';
import { TradingTimeService } from '../../../../services/trading-time.service';
import type { TradingPeriod } from '../../../../components/app-clock/app-clock.model';
import { marketGroupFor, marketScheduleForToday } from '../../../../components/app-clock/utils/clock.utils';
import {
	DEFAULT_SESSION_WINDOW,
	buildTrendChartSeries,
	buildTrendChartView,
	findNearestPoint,
	formatAxisPrice,
	formatAxisTime,
	TREND_CHART_VIEW_SIZE
} from './trend-chart.utils';
import type { SessionTimeWindow } from './trend-chart.model';

/**
 * One symbol, one request per tick. The feed publishes a trade every 30s, so a 60s poll can leave
 * the newest point up to a minute old — acceptable here, since the live last-price line already
 * tracks the current price between polls. The market list runs its sparklines faster
 * (`SPARKLINE_POLL_INTERVAL_MS`, 10s) because those double as the list's live price readout.
 */
const TREND_CHART_POLL_INTERVAL_MS = 60_000;

/**
 * What the card is showing. `loading` is the state before a symbol's first response is in, and is
 * distinct from a `ready` series that turned out to be empty: the first draws the spinner, the
 * second the no-data message.
 */
type TrendChartPhase = 'loading' | 'ready' | 'error';

interface TrendChartState {
	phase: TrendChartPhase;
	trades: IntradayChartPoint[];
}

const LOADING_STATE: TrendChartState = { phase: 'loading', trades: [] };
const FAILED_STATE: TrendChartState = { phase: 'error', trades: [] };

@Component({
	selector: 'symbol-trend-chart',
	templateUrl: './symbol-trend-chart.component.html',
	imports: [AccordionComponent, UiButtonComponent, UiEmptyStateComponent, UIIconComponent, UiLoadingComponent],
	styleUrl: './symbol-trend-chart.component.scss',
	host: {
		class: 'block w-full'
	}
})
export class SymbolTrendChartComponent {
	private readonly symbolsService = inject(SymbolsService);
	private readonly tradingTimeService = inject(TradingTimeService);
	private readonly document = inject(DOCUMENT);
	private readonly symbolDetailLayoutStore = inject(SymbolDetailLayoutStore);
	private readonly router = inject(Router);

	protected readonly sectionLabel = SYMBOL_DETAIL_SECTION_LABELS['trend-chart'];

	readonly isExpanded = computed(() => this.symbolDetailLayoutStore.isExpanded('trend-chart'));

	onExpandedChange(expanded: boolean): void {
		this.symbolDetailLayoutStore.setExpanded('trend-chart', expanded);
	}

	openEasyChart(): void {
		this.router.navigate(['/easy-chart', this.symbolIsin()]);
	}

	readonly symbolIsin = input.required<string>();
	readonly marketGroupId = input<number>();

	readonly lastTradedPrice = input<number>();

	protected readonly VIEW_SIZE = TREND_CHART_VIEW_SIZE;
	protected readonly LINE_STROKE = 'var(--color-slate-600)';
	/** Area under the price line: Blue/050 → Blue/100 top-to-bottom at 30%, per the handoff. */
	protected readonly AREA_GRADIENT_FROM = 'var(--color-blue-100)';
	protected readonly AREA_GRADIENT_TO = 'var(--color-blue-050)';

	/** `<defs>` ids are document-global, so each instance needs its own. */
	protected readonly areaGradientId = `trend-chart-area-${guid()}`;

	/** The layer a touch or a click lands on — the chart box itself, gridlines and line included. */
	private readonly chartSurface = viewChild<ElementRef<HTMLElement>>('chartSurface');

	/**
	 * Rendered width of the chart box. How many time labels the x gutter can carry is a question of
	 * pixels — the same gap that overprints two labels on a phone is clear air on a tablet — so the
	 * box has to be measured rather than assumed.
	 */
	private readonly nowMs = toSignal(timer(0, TREND_CHART_POLL_INTERVAL_MS).pipe(map(() => Date.now())), {
		initialValue: Date.now()
	});

	constructor() {
		// The readout names one trade of one series, so it cannot outlive either: a change of symbol
		// or a collapse drops it rather than letting it reappear over the next chart.
		effect(() => {
			this.symbolIsin();
			this.isExpanded();
			this.selectedTimeMs.set(null);
		});

		// Dismissal: a pointer down anywhere but the chart clears the marker. Bound for as long as
		// something is marked and no longer — a chart with nothing marked has nothing to dismiss, and
		// this is one card on a page of them. The tap that marked a trade has finished dispatching by
		// the time the effect flushes, so it cannot dismiss what it just marked; the containment check
		// is what keeps every later tap on the chart moving the marker instead. Gated on a boolean, so
		// a scrub across the series does not rebind on every trade it passes.
		effect(onCleanup => {
			if (!this.hasSelection()) {
				return;
			}

			const dismiss = (event: PointerEvent): void => {
				if (!this.chartSurface()?.nativeElement.contains(event.target as Node)) {
					this.selectedTimeMs.set(null);
				}
			};

			this.document.addEventListener('pointerdown', dismiss);
			onCleanup(() => this.document.removeEventListener('pointerdown', dismiss));
		});
	}

	/** Bumped by the retry button, which re-enters `pollSeries` from its loading state. */
	private readonly retries = signal(0);

	/**
	 * Current-session series from `/symbols/{isin}/intraday-chart`, re-fetched every 60s while the
	 * accordion is open — a collapsed chart polls nothing.
	 *
	 * A collapse emits nothing at all, so the state survives it untouched. Every other change of
	 * symbol or retry restarts the stream, which reports `loading` before it reports anything else:
	 * the card must never show one symbol's series while another symbol's request is in flight.
	 */
	private readonly state = toSignal(
		toObservable(
			computed(() => (this.isExpanded() ? { isin: this.symbolIsin(), attempt: this.retries() } : undefined))
		).pipe(switchMap(request => (request === undefined ? EMPTY : this.pollSeries(request.isin)))),
		{ initialValue: LOADING_STATE }
	);

	private readonly selectedMarketGroup = computed(() =>
		marketGroupFor(this.tradingTimeService.marketGroups(), this.marketGroupId())
	);

	/** The selected group's continuous trading window for today's exchange weekday. */
	private readonly sessionWindow = computed<SessionTimeWindow | null>(() => {
		if (!this.tradingTimeService.marketGroups()?.length) {
			return DEFAULT_SESSION_WINDOW;
		}

		const periods = marketScheduleForToday(this.selectedMarketGroup(), new Date(this.nowMs())).periods.filter(
			(period): period is TradingPeriod & { type: 'trading' } => period.type === 'trading'
		);
		if (!periods.length) {
			return null;
		}

		return { start: periods[0].start, end: periods[periods.length - 1].end };
	});

	/**
	 * The drawable series — today's trades only, sorted and trimmed to whole-minute ends; see
	 * `buildTrendChartSeries`. Read here rather than inside `view()` so the readout and the spoken
	 * label report the same series the line is drawn from.
	 */
	private readonly validTrades = computed(() =>
		buildTrendChartSeries(this.state().trades, this.nowMs(), this.sessionWindow())
	);

	readonly isLoading = computed(() => this.state().phase === 'loading');

	readonly hasFailed = computed(() => this.state().phase === 'error');

	readonly hasData = computed(() => this.validTrades().length > 0);

	readonly view = computed(() =>
		buildTrendChartView(this.validTrades(), this.nowMs(), this.sessionWindow(), undefined, this.lastTradedPrice())
	);

	/**
	 * Trade the readout is pinned to, held by its timestamp rather than by its index in the series:
	 * the 60s poll appends to the series, and an index would slide onto a neighboring trade.
	 * `null` is nothing selected, which is how the chart starts and what a tap outside restores.
	 */
	private readonly selectedTimeMs = signal<number | null>(null);

	/** Whether a pointer is down on the chart, which is what turns a tap into a scrub. */
	private isScrubbing = false;

	/** Whether anything is marked at all — the dismissal listener's whole reason to be bound. */
	private readonly hasSelection = computed(() => this.selectedTimeMs() !== null);

	/** The marked trade, or `undefined` when nothing is selected — the template draws neither. */
	readonly activePoint = computed(() => {
		const timeMs = this.selectedTimeMs();

		return timeMs === null ? undefined : this.view().points.find(point => point.timeMs === timeMs);
	});

	/**
	 * How the bubble sits on its marker. Centred over it in the middle of the chart, and turned inward
	 * within a fifth of the width of either end — roughly half a bubble — so a trade at the first or
	 * the last tick does not push its readout out of the card. The same inward anchoring the time
	 * axis gives its two bound labels.
	 */
	readonly tooltipAlign = computed(() => {
		const x = this.activePoint()?.x ?? TREND_CHART_VIEW_SIZE / 2;
		const inset = TREND_CHART_VIEW_SIZE / 5;

		if (x < inset) {
			return 'flex-start';
		}

		return x > TREND_CHART_VIEW_SIZE - inset ? 'flex-end' : 'center';
	});

	/**
	 * A marker in the top two fifths of the box has no room for a bubble above it, so the bubble is
	 * drawn under it instead — the card must not grow to make the room.
	 */
	readonly isTooltipBelow = computed(
		() => (this.activePoint()?.y ?? TREND_CHART_VIEW_SIZE) < (TREND_CHART_VIEW_SIZE * 2) / 5
	);

	/**
	 * A touch or a click marks the trade nearest it, and a pointer held down scrubs the marker along
	 * the series. `touch-action: pan-y` on the surface is what makes the scrub possible on a phone:
	 * the vertical gesture stays with the page, so the chart only ever claims the horizontal one.
	 */
	protected onChartPointerDown(event: PointerEvent): void {
		const surface = event.currentTarget as HTMLElement;

		this.isScrubbing = true;
		// Keeps the move events coming after the finger leaves the box, so a scrub that strays above
		// the chart goes on tracking instead of freezing at the edge.
		surface.setPointerCapture(event.pointerId);
		this.markNearest(surface, event.clientX);
	}

	protected onChartPointerMove(event: PointerEvent): void {
		if (this.isScrubbing) {
			this.markNearest(event.currentTarget as HTMLElement, event.clientX);
		}
	}

	/** Release, or a gesture the browser took over for a scroll. The marker stays where it landed. */
	protected onChartPointerEnd(): void {
		this.isScrubbing = false;
	}

	/** Marks the trade nearest `clientX`, read as a fraction of the surface's own width. */
	private markNearest(surface: HTMLElement, clientX: number): void {
		const { left, width } = surface.getBoundingClientRect();

		if (width <= 0) {
			return;
		}

		const ratio = Math.min(Math.max((clientX - left) / width, 0), 1);
		const nearest = findNearestPoint(this.view().points, ratio);

		this.selectedTimeMs.set(nearest?.timeMs ?? null);
	}

	/**
	 * The chart is one `role="img"`, which drops its own tick labels from the accessibility tree, so
	 * the label has to say what the gridlines say: the span covered, the prices reached and which
	 * way the line ended. Read off the trades rather than off `view()`, whose axes are rounded
	 * outward onto gridlines the series never actually touched.
	 */
	readonly chartLabel = computed(() => {
		const trades = this.validTrades();

		if (!trades.length) {
			return 'نمودار تغییرات روزانه قیمت';
		}

		const first = trades[0];
		const last = trades[trades.length - 1];
		const prices = trades.map(trade => trade.price);
		const trend = last.price > first.price ? 'صعودی' : last.price < first.price ? 'نزولی' : 'بدون تغییر';

		return (
			`نمودار تغییرات روزانه قیمت، از ساعت ${formatAxisTime(first.timeMs)} تا ${formatAxisTime(last.timeMs)}، ` +
			`کمترین ${formatAxisPrice(Math.min(...prices))}، بیشترین ${formatAxisPrice(Math.max(...prices))}، ` +
			`روند ${trend}`
		);
	});

	retry(): void {
		this.retries.update(attempt => attempt + 1);
	}

	/**
	 * One symbol's lifecycle: `loading` straight away, then a series on every poll.
	 *
	 * A failure surfaces as the error state only while nothing has been drawn yet. Once a response
	 * has landed a failed tick is held back and the card left as it was — a 60s poll on a flaky
	 * connection would otherwise keep replacing a good chart with an error — and the next tick
	 * simply retries. A `ready` session that reported no trades counts as drawn: its no-data
	 * message must not flip to a retry button for a request that never failed.
	 */
	private pollSeries(isin: string): Observable<TrendChartState> {
		return concat(
			of(LOADING_STATE),
			timer(0, TREND_CHART_POLL_INTERVAL_MS).pipe(
				switchMap(() =>
					this.symbolsService.getIntradayChart(isin).pipe(
						map(chart => ({ phase: 'ready' as const, trades: chart?.points ?? [] })),
						catchError(() => of(FAILED_STATE))
					)
				),
				scan(
					(previous: TrendChartState, next: TrendChartState) =>
						next.phase === 'error' && previous.phase === 'ready' ? previous : next,
					LOADING_STATE
				)
			)
		);
	}
}
